// IndexedDB storage of the offline layer (no library): the outbox, the local → server id map,
// photo blobs waiting for upload, and the read cache. Same interface as createMemoryStore in
// ./outboxCore, which takes over where IndexedDB is unavailable (some private modes): the app
// still works offline for the session, it just does not survive a reload there.
import { createMemoryStore } from "./outboxCore";

const DB_NAME = "leistfusion-offline";
const DB_VERSION = 1;
const STORES = {
  outbox: "id",
  ids: "localId",
  blobs: "id",
  cache: "key"
};

const request = req =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const openDb = () =>
  new Promise((resolve, reject) => {
    const open = indexedDB.open(DB_NAME, DB_VERSION);
    open.onupgradeneeded = () => {
      const db = open.result;
      Object.entries(STORES).forEach(([name, keyPath]) => {
        if (!db.objectStoreNames.contains(name)) db.createObjectStore(name, { keyPath });
      });
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error);
    open.onblocked = () => reject(new Error("IndexedDB blocked"));
  });

export function createIdbStore() {
  const memory = createMemoryStore();
  let dbPromise = null;
  const db = () => {
    if (!dbPromise) {
      dbPromise =
        typeof indexedDB === "undefined"
          ? Promise.resolve(null)
          : openDb().catch(error => {
              console.warn("Offline storage unavailable, keeping changes in memory:", error);
              return null;
            });
    }
    return dbPromise;
  };

  // Runs fn(objectStore) in one transaction; null db → the memory fallback
  const run = async (name, mode, fn, fallback) => {
    const database = await db();
    if (!database) return fallback();
    const tx = database.transaction(name, mode);
    const result = await request(fn(tx.objectStore(name)));
    if (mode === "readwrite") {
      await new Promise((resolve, reject) => {
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      });
    }
    return result;
  };

  return {
    all: () => run("outbox", "readonly", s => s.getAll(), memory.all),
    put: item =>
      run(
        "outbox",
        "readwrite",
        s => s.put(item),
        () => memory.put(item)
      ),
    remove: id =>
      run(
        "outbox",
        "readwrite",
        s => s.delete(id),
        () => memory.remove(id)
      ),
    getId: async localId => {
      const row = await run(
        "ids",
        "readonly",
        s => s.get(localId),
        async () => {
          const id = await memory.getId(localId);
          return id ? { id } : null;
        }
      );
      return row ? row.id : null;
    },
    putId: (localId, id) =>
      run(
        "ids",
        "readwrite",
        s => s.put({ localId, id, at: Date.now() }),
        () => memory.putId(localId, id)
      ),
    // { buffer: ArrayBuffer, type } — older WebKit cannot store Blob objects reliably
    putBlob: (id, value) =>
      run(
        "blobs",
        "readwrite",
        s => s.put({ id, ...value }),
        () => memory.putBlob(id, value)
      ),
    getBlob: id =>
      run(
        "blobs",
        "readonly",
        s => s.get(id),
        () => memory.getBlob(id)
      ),
    removeBlob: id =>
      run(
        "blobs",
        "readwrite",
        s => s.delete(id),
        () => memory.removeBlob(id)
      ),
    getCache: key =>
      run(
        "cache",
        "readonly",
        s => s.get(key),
        () => memory.getCache(key)
      ),
    putCache: (key, value) =>
      run(
        "cache",
        "readwrite",
        s => s.put({ key, ...value }),
        () => memory.putCache(key, value)
      ),
    clearCache: () => run("cache", "readwrite", s => s.clear(), memory.clearCache)
  };
}
